import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Loader, MailCheck, RefreshCw, ShieldOff, Trash2, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ConfirmationModal } from '../ConfirmationModal';
import { SectionHeader } from '../ui/SectionHeader';
import { dataControllerApiService, type ErasureOutcome, type ErasureRecord } from '../../services/dataControllerApi';
import { runBrowserErasureSteps } from '../../services/dataControllerErasure';
import type { SubjectIdentity } from '../../types/dataController';
import { cn } from '../../utils/cn';

/**
 * Data Controller · "Delete and de-identify" — one button whose label follows the request's state:
 *
 *   (none)                → "Delete and de-identify…": confirm, then the backend opens the request and
 *                           Tesseract emails the account holder a confirmation link
 *   awaiting_confirmation → waits for the holder; staff can check again, record an override with a
 *                           reason, or cancel
 *   confirmed/overridden  → "Run deletion": confirm, then the backend de-identifies its rows and
 *                           Tesseract deletes the account; the browser then runs the Redis and
 *                           Elasticsearch steps and reports them
 *   completed             → the outcomes, grouped by system, and what was kept
 */

type Busy = 'request' | 'override' | 'cancel' | 'run' | 'browser' | 'refresh' | null;

const ACTION_META: Record<ErasureOutcome['action'], { label: string; badge: string }> = {
  deleted: { label: 'Deleted', badge: 'badge-danger' },
  'de-identified': { label: 'De-identified', badge: 'badge-warning' },
  retained: { label: 'Kept', badge: 'badge-neutral' },
  nothing: { label: 'Nothing held', badge: 'badge-neutral' },
  failed: { label: 'Failed', badge: 'badge-danger' },
  manual: { label: 'By hand', badge: 'badge-warning' },
};

const when = (ts: number | null | undefined) => (ts ? format(new Date(ts), 'PPp') : '—');

const describeError = (e: unknown) => (e instanceof Error ? e.message : String(e));

const OutcomeRows = ({ outcomes }: { outcomes: ErasureOutcome[] }) => (
  <ul className="divide-y divide-glass-200/10">
    {outcomes.map((o, i) => {
      const meta = ACTION_META[o.action];
      return (
        <li key={`${o.system}-${o.step}-${i}`} className="px-4 py-2 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="text-sm text-white">{o.step}</span>
              <span className="text-xs text-glass-500">{o.system}</span>
            </div>
            {o.detail && <div className={cn('text-xs mt-0.5 break-words', o.action === 'failed' ? 'text-red-300' : 'text-glass-400')}>{o.detail}</div>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {typeof o.count === 'number' && o.action !== 'retained' && <span className="text-xs text-glass-400 tabular-nums">{o.count.toLocaleString('en-AU')}</span>}
            <span className={cn('badge', meta.badge)}>{meta.label}</span>
          </div>
        </li>
      );
    })}
  </ul>
);

const Outcomes = ({ record, live }: { record: ErasureRecord; live: ErasureOutcome[] | null }) => {
  const [showKept, setShowKept] = useState(false);
  const all = useMemo(() => [...(record.result.argus ?? []), ...(record.result.tesseract ?? []), ...(record.result.redis ?? []), ...(record.result.client ?? live ?? [])], [record, live]);
  const acted = all.filter((o) => o.action !== 'retained' && o.action !== 'nothing');
  const quiet = all.filter((o) => o.action === 'retained' || o.action === 'nothing');
  if (all.length === 0) return null;
  return (
    <div className="space-y-3">
      <div className="subcard">
        <div className="px-4 py-2.5 border-b border-glass-200/10 flex items-center justify-between">
          <span className="eyebrow">Deleted and de-identified</span>
          <span className="text-xs text-glass-500 tabular-nums">{acted.length} steps</span>
        </div>
        {acted.length ? <OutcomeRows outcomes={acted} /> : <p className="px-4 py-3 text-sm text-glass-400">Nothing needed to change.</p>}
      </div>
      <div className="subcard">
        <button type="button" className="w-full px-4 py-2.5 flex items-center justify-between text-left" onClick={() => setShowKept((v) => !v)}>
          <span className="eyebrow">Kept, and nothing held</span>
          <span className="text-xs text-glass-500 flex items-center gap-1">
            {quiet.length} {showKept ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </span>
        </button>
        {showKept && <div className="border-t border-glass-200/10"><OutcomeRows outcomes={quiet} /></div>}
      </div>
    </div>
  );
};

export const ErasurePanel = ({ subject }: { subject: SubjectIdentity }) => {
  const uuid = subject.uuid;
  const [record, setRecord] = useState<ErasureRecord | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [modal, setModal] = useState<'request' | 'run' | 'cancel' | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [live, setLive] = useState<ErasureOutcome[] | null>(null);
  // A player who never linked Minecraft still has a website account under their email; staff can
  // name it here and it is treated as this player's.
  const [accountEmail, setAccountEmail] = useState(subject.email ?? '');
  const [accountUsername, setAccountUsername] = useState(subject.tesseractUsername ?? '');

  const load = useCallback(
    async (quiet: boolean) => {
      if (!quiet) setBusy('refresh');
      try {
        const next = await dataControllerApiService.getErasure(uuid);
        setRecord(next);
        setError(null);
      } catch (e) {
        if (!quiet) setError(describeError(e));
      } finally {
        setLoaded(true);
        if (!quiet) setBusy(null);
      }
    },
    [uuid]
  );

  useEffect(() => {
    setRecord(null);
    setLoaded(false);
    setNotice(null);
    setError(null);
    void load(true);
  }, [load]);

  useEffect(() => {
    setAccountEmail(subject.email ?? '');
    setAccountUsername(subject.tesseractUsername ?? '');
  }, [subject.email, subject.tesseractUsername]);

  // While the holder has the email, look for their answer every half minute.
  useEffect(() => {
    if (record?.status !== 'awaiting_confirmation' || record.expired) return;
    const timer = window.setInterval(() => void load(true), 30_000);
    return () => window.clearInterval(timer);
  }, [record?.status, record?.expired, load]);

  const request = useCallback(async () => {
    setBusy('request');
    setError(null);
    try {
      const email = accountEmail.trim().toLowerCase();
      const websiteUsername = accountUsername.trim();
      const { erasure, reused, email_sent } = await dataControllerApiService.requestErasure(uuid, {
        username: subject.username,
        discord_id: subject.discordId,
        email: email.includes('@') ? email : null,
        tesseract_id: subject.tesseractId,
        tesseract_username: websiteUsername || null,
      });
      setRecord(erasure);
      const assumed = erasure.subject?.assumedLink ? ` The account was matched by ${erasure.subject.accountMatchedBy === 'username' ? 'website username' : 'email'} and has no linked Minecraft account, so it is treated as this player's.` : '';
      if (reused) setNotice('A request was already open for this player; showing it.');
      else if (!erasure.has_account) setNotice('No website account matched this player, so no confirmation email could be sent. Enter the website account email or username above and try again, or record an override once the person has confirmed another way.');
      else if (email_sent) setNotice(`Confirmation email sent to ${erasure.holder_email_masked ?? 'the account holder'}. Nothing is deleted until they confirm, or an override is recorded.${assumed}`);
      else setNotice(`The request is open but the confirmation email could not be sent. Try again later or record an override once the person has confirmed another way.${assumed}`);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(null);
      setModal(null);
    }
  }, [uuid, subject, accountEmail, accountUsername]);

  const override = useCallback(async () => {
    if (!record) return;
    setBusy('override');
    setError(null);
    try {
      setRecord(await dataControllerApiService.overrideErasure(record.id, overrideReason.trim()));
      setOverrideOpen(false);
      setOverrideReason('');
      setNotice('Override recorded. The deletion can be run now.');
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(null);
    }
  }, [record, overrideReason]);

  const cancel = useCallback(async () => {
    if (!record) return;
    setBusy('cancel');
    setError(null);
    try {
      setRecord(await dataControllerApiService.cancelErasure(record.id));
      setNotice('Request cancelled. Nothing was deleted.');
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(null);
      setModal(null);
    }
  }, [record]);

  const runBrowserSteps = useCallback(
    async (id: string) => {
      setBusy('browser');
      setLive([]);
      try {
        const steps = await runBrowserErasureSteps({ uuid, discordId: subject.discordId, onProgress: setLive });
        setRecord(await dataControllerApiService.reportErasureClientSteps(id, steps));
        const failed = steps.filter((s) => s.action === 'failed');
        setNotice(failed.length ? `Deletion recorded. ${failed.length} browser step${failed.length === 1 ? '' : 's'} failed and must be finished by hand; see below.` : 'Deletion complete and recorded.');
      } catch (e) {
        setError(`The deletion ran, but the browser steps could not be recorded: ${describeError(e)}`);
      } finally {
        setLive(null);
        setBusy(null);
      }
    },
    [uuid, subject.discordId]
  );

  // The server-side run is a series of short calls: the first de-identifies our rows, deletes the
  // Tesseract account and clears the Redis profile; each call after that scans a few clusters'
  // session logs, until the record is no longer `running`. The record is refreshed after every
  // call, so the outcomes and the clusters left show live.
  const run = useCallback(async () => {
    if (!record) return;
    setBusy('run');
    setError(null);
    setModal(null);
    let next: ErasureRecord;
    try {
      next = await dataControllerApiService.runErasure(record.id);
      setRecord(next);
      while (next.status === 'running') {
        next = await dataControllerApiService.runErasure(next.id);
        setRecord(next);
      }
    } catch (e) {
      setError(describeError(e));
      setBusy(null);
      void load(true);
      return;
    }
    if (next.status !== 'completed') {
      setBusy(null);
      return;
    }
    await runBrowserSteps(next.id);
  }, [record, load, runBrowserSteps]);

  const state = record?.status ?? 'none';
  const open = state === 'awaiting_confirmation' || state === 'confirmed' || state === 'overridden' || state === 'running';
  const canRun = state === 'confirmed' || state === 'overridden' || state === 'running';
  const disabled = busy !== null;
  const pendingClusters = record?.redis_pending?.length ?? 0;
  const scannedClusters = (record?.result.redis ?? []).filter((o) => /^session log entries \(/.test(o.step)).length;

  const statusLine = (() => {
    if (!record) return null;
    switch (record.status) {
      case 'awaiting_confirmation':
        if (!record.has_account) return { icon: AlertCircle, tone: 'text-yellow-400', text: `Opened ${when(record.requested_at)} by ${record.requested_by}. No website account matched, so no confirmation email could be sent: record an override once the person has confirmed another way.` };
        if (record.expired) return { icon: AlertCircle, tone: 'text-yellow-400', text: `The confirmation link sent to ${record.holder_email_masked ?? 'the holder'} expired ${when(record.expires_at)} without an answer. Cancel and open a new request, or record an override.` };
        return { icon: MailCheck, tone: 'text-accent-300', text: `Confirmation email sent to ${record.holder_email_masked ?? 'the account holder'} on ${when(record.requested_at)}; waiting for them to confirm (link valid until ${when(record.expires_at)}).${record.subject?.assumedLink ? ` The website account was matched by ${record.subject.accountMatchedBy === 'username' ? 'website username' : 'email'} and has no linked Minecraft account; it is treated as this player's.` : ''}` };
      case 'confirmed':
        return { icon: CheckCircle2, tone: 'text-green-400', text: `The account holder confirmed by email on ${when(record.confirmed_at)}. Nothing has been deleted yet.` };
      case 'overridden':
        return { icon: ShieldOff, tone: 'text-yellow-400', text: `Override recorded by ${record.overridden_by} on ${when(record.overridden_at)}: “${record.override_reason}”. Nothing has been deleted yet.` };
      case 'running':
        return { icon: Loader, tone: 'text-accent-300', text: `Deletion started ${when(record.executed_at)} by ${record.executed_by}. The website account and this system's rows are done; ${scannedClusters} of ${scannedClusters + pendingClusters} cluster session logs scanned so far.${busy === 'run' ? '' : ' It was interrupted before it finished: press Continue deletion to scan the rest.'}` };
      case 'completed':
        return { icon: record.redis_needs_attention ? AlertCircle : CheckCircle2, tone: record.redis_needs_attention ? 'text-yellow-400' : 'text-green-400', text: `Deleted ${when(record.executed_at)} by ${record.executed_by}${record.confirmation === 'override' ? ' (override)' : ' (confirmed by email)'}.${record.client_steps_pending ? ' The browser steps have not been recorded yet.' : ''}${record.result.completion_email_sent ? ' The holder was emailed that it is done.' : ''}${record.redis_needs_attention ? ' A Redis step needs finishing by hand; see below.' : ''}` };
      case 'cancelled':
        return { icon: XCircle, tone: 'text-glass-400', text: `Cancelled ${when(record.cancelled_at)} by ${record.cancelled_by}. Nothing was deleted.` };
      default:
        return null;
    }
  })();

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader
        title="Delete and de-identify"
        icon={Trash2}
        description="Carries out a deletion request: the website account and everything that identifies the person are deleted, community records are de-identified, and moderation, audit, payment and Minecraft-keyed gameplay records are kept, as the privacy policy sets out. The account holder confirms by email first unless an override is recorded."
      />

      {!loaded ? (
        <p className="text-sm text-glass-400 flex items-center gap-2">
          <Loader className="w-4 h-4 animate-spin" /> Checking for an existing request…
        </p>
      ) : (
        <div className="space-y-4">
          {record && statusLine && (
            <div className="flex items-start gap-3">
              <statusLine.icon className={cn('w-4 h-4 mt-0.5 flex-shrink-0', statusLine.tone)} />
              <div className="min-w-0 text-sm text-glass-300">
                <div className="eyebrow mb-0.5">Reference {record.reference}</div>
                {statusLine.text}
              </div>
            </div>
          )}

          {(state === 'none' || state === 'cancelled' || (state === 'completed' && !record?.client_steps_pending)) && !subject.tesseractId && (
            <div className="subcard p-4">
              <p className="text-xs text-glass-400 mb-2">
                {subject.email ? 'The website account below will be asked to confirm.' : 'No website account is linked to this Minecraft account. If the person has one, enter its email or website username: it will be treated as theirs and asked to confirm.'}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-glass-400 mb-1" htmlFor="erasure-account-email">Website account email</label>
                  <input id="erasure-account-email" className="glass-input w-full" type="email" value={accountEmail} onChange={(e) => setAccountEmail(e.target.value)} placeholder="name@example.com" disabled={disabled} autoComplete="off" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-glass-400 mb-1" htmlFor="erasure-account-username">Website username</label>
                  <input id="erasure-account-username" className="glass-input w-full" value={accountUsername} onChange={(e) => setAccountUsername(e.target.value)} placeholder="craftdownunder.co username" disabled={disabled} autoComplete="off" />
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {(state === 'none' || state === 'cancelled' || (state === 'completed' && !record?.client_steps_pending)) && (
              <button type="button" className="glass-button bg-red-500/85 hover:bg-red-500 border-red-400/40 text-on-solid" onClick={() => setModal('request')} disabled={disabled}>
                {busy === 'request' ? <Loader className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {state === 'completed' ? 'Delete and de-identify again…' : 'Delete and de-identify…'}
              </button>
            )}
            {canRun && (
              <button type="button" className="glass-button bg-red-500/85 hover:bg-red-500 border-red-400/40 text-on-solid" onClick={() => (state === 'running' ? void run() : setModal('run'))} disabled={disabled}>
                {busy === 'run' || busy === 'browser' ? <Loader className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {busy === 'run'
                  ? state === 'running'
                    ? `Deleting… ${scannedClusters}/${scannedClusters + pendingClusters} clusters`
                    : 'Deleting…'
                  : busy === 'browser'
                    ? 'Running browser steps…'
                    : state === 'running'
                      ? 'Continue deletion'
                      : 'Run deletion now'}
              </button>
            )}
            {state === 'completed' && record?.client_steps_pending && (
              <button type="button" className="btn-primary" onClick={() => void runBrowserSteps(record.id)} disabled={disabled}>
                {busy === 'browser' ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {busy === 'browser' ? 'Running browser steps…' : 'Run the remaining browser steps'}
              </button>
            )}
            {state === 'awaiting_confirmation' && (
              <>
                <button type="button" className="glass-button" onClick={() => void load(false)} disabled={disabled}>
                  {busy === 'refresh' ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Check for confirmation
                </button>
                <button type="button" className="glass-button-secondary" onClick={() => setOverrideOpen((v) => !v)} disabled={disabled}>
                  <ShieldOff className="w-4 h-4" />
                  Override…
                </button>
              </>
            )}
            {open && (
              <button type="button" className="glass-button-secondary" onClick={() => setModal('cancel')} disabled={disabled}>
                <XCircle className="w-4 h-4" />
                Cancel request
              </button>
            )}
          </div>

          {overrideOpen && state === 'awaiting_confirmation' && (
            <form
              className="subcard p-4 space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
                void override();
              }}
            >
              <label className="block text-xs font-medium text-glass-400" htmlFor="erasure-override-reason">
                Why the email confirmation is being skipped
              </label>
              <textarea id="erasure-override-reason" className="glass-input w-full min-h-[4.5rem]" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="e.g. The account holder confirmed by replying to the request email on 11 September 2026." maxLength={500} disabled={disabled} />
              <p className="text-xs text-glass-500">Recorded on the deletion record and in the audit log with your name. Five characters minimum.</p>
              <div className="flex gap-2">
                <button type="submit" className="btn-primary" disabled={disabled || overrideReason.trim().length < 5}>
                  {busy === 'override' ? <Loader className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />}
                  Record override
                </button>
                <button type="button" className="glass-button-secondary" onClick={() => setOverrideOpen(false)} disabled={disabled}>
                  Keep waiting
                </button>
              </div>
            </form>
          )}

          {notice && !error && (
            <div className="alert alert-warning">
              <AlertCircle />
              <span>{notice}</span>
            </div>
          )}
          {error && (
            <div className="alert alert-danger">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          {record && (record.status === 'completed' || record.status === 'running' || live) && <Outcomes record={record} live={live} />}
        </div>
      )}

      {modal === 'request' && (
        <ConfirmationModal
          title="Delete and de-identify this player?"
          message={`This opens a deletion request for ${subject.username ?? subject.uuid}. ${subject.tesseractId || accountEmail.trim() || accountUsername.trim() ? 'Tesseract will email the account holder a confirmation link; nothing is deleted until they confirm or you record an override.' : 'If a website account matches, its holder is emailed a confirmation link; otherwise you will need to record an override.'} Once run, the website account, its linked identities, sign-in history and preferences are gone for good: ranks, prefixes, playtime perks, CDU Plus perks, event points and referral rewards cannot be restored.`}
          confirmText="Open request"
          isLoading={busy === 'request'}
          onClose={() => setModal(null)}
          onConfirm={() => void request()}
        />
      )}
      {modal === 'run' && record && (
        <ConfirmationModal
          title="Run the deletion now?"
          message={`Reference ${record.reference}, ${record.confirmation === 'override' ? 'confirmed by staff override' : 'confirmed by the account holder'}. This deletes the Tesseract account and everything that identifies ${subject.username ?? 'the player'}, de-identifies community records, removes the IP history and preferences from Redis over the game tunnel, and clears the search index and shop changelog identifiers from this browser. It cannot be undone.`}
          confirmText="Delete now"
          isLoading={busy === 'run'}
          onClose={() => setModal(null)}
          onConfirm={() => void run()}
        />
      )}
      {modal === 'cancel' && record && (
        <ConfirmationModal
          title="Cancel this deletion request?"
          message={`Reference ${record.reference} will be closed without deleting anything. The account holder is not notified.`}
          confirmText="Cancel request"
          cancelText="Keep it open"
          isLoading={busy === 'cancel'}
          onClose={() => setModal(null)}
          onConfirm={() => void cancel()}
        />
      )}
    </div>
  );
};
